import { AppstoreOutlined, BarsOutlined, CaretLeftOutlined, CaretRightFilled } from "@ant-design/icons";
import { Button, Empty, Radio, Typography } from "antd";
import { showError } from "common/ToastNotifications";
import { BasicFormBuilder, LoadingOverlay, ProhibitedArea } from "components";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import MenuPanelContainer from "components/atoms/MenuPanelContainer";
import Segment from "components/atoms/Segment";
import SidePanel from "components/atoms/SidePanel";
import SuccessResult from "components/atoms/SuccessResult";
import Tooltip from "components/atoms/Tooltip";
import AttachmentTab from "components/molecules/AttachmentTab";
import DataObjectSearchDuplicateForm from "components/molecules/DataObjectSearchDuplicateForm";
import DeduplicatedRecordsTable from "components/molecules/DeduplicatedRecordsTable";
import RunDeduplicationModal, { IRunDeduplicationOptions } from "components/molecules/RunDeduplicationModal";
import BirthToggle from "components/molecules/BirthToggle";
import DuplicateDataObjectComparison from "components/molecules/DuplicateDataObjectComparison";
import DuplicateDataObjectJobClusteredView from "components/molecules/DuplicateDataObjectJobClusteredView";
import DuplicateDataObjectList from "components/molecules/DuplicateDataObjectList";
import DuplicateDataObjectRecordClusteredView from "components/molecules/DuplicateDataObjectRecordClusteredView";
import OkCancelConfirmationBox from "components/molecules/OkCancelConfirmationBox";
import DuplicateDataObjectJobList from "components/organisms/DuplicateDataObjectJobList";
import DataObjectDuplicateSearchPageBreadcrumb from "components/pages/DataObjectDuplicateSearchPage/DataObjectDuplicateSearchPageBreadcrumb";
import AppPaths from "constants/appPaths";
import Annotations from "containers/Annotations";
import Comments from "containers/Comments";
import _ from "lodash";
import { Component } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { Action, Dispatch } from "redux";
import actions from "store/actions";
import AppState from "store/AppState";
import CypressTestIds from "../cypress/CypressTestIds";
import DataObjectDuplicateMergePreview from "./DataObjectDuplicateMergePreview";
import DataObjectDuplicateMergeRules from "./DataObjectDuplicateMergeRules";
import DataObjectDuplicateSummary from "./DataObjectDuplicateSummary";

const mapStateToProps = (state: AppState) => {
    return {
        getUserSearchDataObjectsPermisionResult: state.dataObjectSearch.getUserSearchDataObjectsPermisionResult,
        userSearchDataObjectsPermision: state.dataObjectSearch.userSearchDataObjectsPermision,
        userBusinessAreas: state.home.businessAreas,
        getSchemaModelsResult: state.schemaModels.getSchemaModelsResult,
        schemaModelListResult: state.schemaModels.schemaModelListResult,
        getSchemaDataSetResult: state.dataSets.getSchemaDataSetResult,
        schemaDataSetResult: state.dataSets.schemaDataSetResult,
        getDataObjectResult: state.dataObjects.getDataObjectResult,
        dataObject: state.dataObjects.dataObject,
        getSchemaModelResult: state.schemaModels.getSchemaModelResult,
        schemaModel: state.schemaModels.schemaModel,
        getDataTagsByBusinessAreaIdResult: state.dataTags.getDataTagsByBusinessAreaIdResult,
        dataTagsByBusinessAreaIdResult: state.dataTags.dataTagsByBusinessAreaIdResult,
        getUserBusinessAreaDataTagsResult: state.dataTags.getUserBusinessAreaDataTagsResult,
        userBusinessAreaDataTags: state.dataTags.userBusinessAreaDataTags,
        getDataObjectsDuplicateJobsResult: state.dataObjectDuplicateSearch.getDataObjectsDuplicateJobsResult,
        dataObjectsDuplicateJobsResult: state.dataObjectDuplicateSearch.dataObjectsDuplicateJobsResult,
        searchDuplicateDataObjectJobsResult: state.dataObjectDuplicateSearch.searchDuplicateDataObjectJobsResult,
        getDuplicateDataObjectsResultSetResult: state.dataObjectDuplicateSearch.getDuplicateDataObjectsResultSetResult,
        duplicateDataObjectsResultSet: state.dataObjectDuplicateSearch.duplicateDataObjectsResultSet,
        getDuplicateDataObjectsResultSetNextPageResult: state.dataObjectDuplicateSearch.getDuplicateDataObjectsResultSetNextPageResult,
        restartJobResult: state.jobs.restartJobResult,
        cancelJobResult: state.jobs.cancelJobResult,
        runDeDuplicateJobResult: state.dataObjectDuplicateSearch.runDeDuplicateJobResult,
        getDeDuplicatedRecordsRequestResult: state.dataObjectDuplicateSearch.getDeDuplicatedRecordsRequestResult,
        deDuplicatedRecordsResult: state.dataObjectDuplicateSearch.deDuplicatedRecordsResult,
        removeSurvivalRecordNominationResult: state.dataObjectDuplicateSearch.removeSurvivalRecordNominationResult,
        getCommentsPermissionResult: state.comments.getCommentsPermissionResult,
        userCommentsPermission: state.comments.userCommentsPermission,
        // A run about to start, and whether it waits for its delete options (the tenant's Golden
        // Nominal maintenance switch, read when the run was asked for).
        deduplicationRunPreparation: (state.dataObjectDuplicateSearch as { deduplicationRunPreparation?: { duplicateSetId: string; askOptions: boolean } | null })
            .deduplicationRunPreparation,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action<string>>) => {
    return {
        getUserSearchDataObjectsPermission: () => dispatch(actions.getUserSearchDataObjectsPermissionRequest()),
        getDataObjectsDuplicateJobs: () => dispatch(actions.getDataObjectsDuplicateJobsRequest()),
        getDuplicateDataObjectsResultSet: (jobId: string) => dispatch(actions.getDuplicateDataObjectsResultSetRequest(jobId)),
        getDuplicateDataObjectsResultSetNextPage: (jobId: string, pageKey: string) => dispatch(actions.getDuplicateDataObjectsResultSetNextPageRequest(jobId, pageKey)),
        restartJob: (jobId: string, version: string, carryRunInfo: boolean, undo: boolean) => dispatch(actions.restartJobRequest(jobId, version, carryRunInfo, undo)),
        cancelJob: (jobId: string, version: string) => dispatch(actions.cancelJobRequest(jobId, version)),
        searchDuplicateDataObjects: (name: string, description: string, businessAreaId: string, schemaId: string, dataSetId: string, dataTags: IDataTag[]) =>
            dispatch(actions.searchDuplicateDataObjectsRequest(name, description, businessAreaId, schemaId, dataSetId, dataTags)),
        getSchemaModels: (businessAreaId: string) => dispatch(actions.getSchemaModelsRequest(businessAreaId)),
        getDataSetBySchemaId: (businessAreaId: string, schemaId: string) => dispatch(actions.getDataSetBySchemaIdRequest(businessAreaId, schemaId)),
        getSchemaModel: (businessAreaId: string, schemaId: string) => dispatch(actions.getSchemaModelRequest(businessAreaId, schemaId)),
        getDataObject: (businessAreaId: string, schemaId: string, dataSetId: string, dataObjectId: string) =>
            dispatch(actions.getDataObjectRequest(businessAreaId, schemaId, dataSetId, dataObjectId)),
        getDataTagsByBusinessAreaId: (businessAreaId: string) => dispatch(actions.getDataTagsByBusinessAreaIdRequest(businessAreaId)),
        getUserBusinessAreaDataTags: () => dispatch(actions.getUserBusinessAreaDataTagsRequest()),
        nominateSurvivalRecord: (duplicateSetId: string, duplicateSetVersion: string, groupId: string, dataObjectId: string, deduplicationOption: string) =>
            dispatch(actions.nominateSurvivalRecordRequest(duplicateSetId, duplicateSetVersion, groupId, dataObjectId, deduplicationOption)),
        removeSurvivalRecordNomination: (duplicateSetId: string, duplicateSetVersion: string, groupId: string, dataObjectId: string) =>
            dispatch(actions.removeSurvivalRecordNominationRequest(duplicateSetId, duplicateSetVersion, groupId, dataObjectId)),
        runDeDuplicateJob: (duplicateSetId: string, deleteDuplicates?: IRunDeduplicationOptions) => dispatch(actions.runDeDuplicateJobRequest(duplicateSetId, deleteDuplicates || null)),
        prepareDeduplicationRun: (duplicateSetId: string) => dispatch(actions.prepareDeduplicationRunRequest(duplicateSetId)),
        clearDeduplicationRunPreparation: () => dispatch(actions.clearDeduplicationRunPreparation()),
        getDeDuplicatedRecords: (survivedRecordId: string) => dispatch(actions.getDeDuplicatedRecordsRequest(survivedRecordId)),
        getCommentsPermission: () => dispatch(actions.getCommentsPermissionRequest()),
    };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type IPropsFromRedux = ConnectedProps<typeof connector>;

interface IDataObjectDuplicateSearchProps extends IPropsFromRedux {
    setBreadcrumbNavigationItems: (items: IBreadcrumbItem[]) => void;
}

interface IDataObjectDuplicateSearchStates {
    busy?: boolean;
    fetchingPermission: boolean;
    busyMessage: string;
    selectedDataObject: {
        id: string;
        name: string;
        fetchingResultSet: boolean;
        error?: string | null;
        loadingAllData?: boolean;
        job?: IDataObjectsDuplicateJob;
        selectedDataObject?: IDataObjectRecord;
        dataTags: string[];
    } | null;
    dataTags: IDataTag[];
    showSidePanelTabs: boolean;
    sidePanelActiveTab: string; // "DATASET_RECORD",
    permissionDenied?: boolean;
    fetchingDataObjectsDuplicateJobs?: boolean;
    loadingDataTags?: boolean;
    loadingSchemaList?: boolean;
    loadingDataSetList?: boolean;
    dataObjectPermissionDenied?: boolean;
    fetchingDataObject?: boolean;
    schemaPermissionDenied?: boolean;
    fetchingSchemaModel?: boolean;
    fetchingDeDuplicatedRecords?: boolean;
    fetchingCommentsPermission?: boolean;
    submittingSearchDuplicateDataObjectRequest?: boolean;
    showDeduplicateJobCreatedResult?: boolean;
    creatingDeduplicationJob?: boolean;
    // The search whose deduplication waits for its delete options (Golden Nominal maintenance on).
    deduplicationOptionsFor?: string | null;
    summaryJob: IDataObjectsDuplicateJob | null;
    showDataObjectFormContainer?: boolean;
    searchInput?: IDataObjectDuplicateSearchInput;
    restartedJob: IDataObjectsDuplicateJob | null;
    showJobRestartConfirmation?: boolean;
    jobRestartConfirmMessage?: string;
    deletedJob: IDataObjectsDuplicateJob | null;
    showJobDeleteConfirmation?: boolean;
    deleteJobConfirmationMessage?: string;
    nominatedPreviewDuplicateSetId: string | null;
    nominatedPreviewDuplicateSetVersion: string | null;
    nominatedPreviewDuplicateSetGroupId: string | null;
    showMergeRules?: boolean;
    mergeRulesProps: {
        businessAreaId?: string;
        schemaId?: string;
        duplicateSetId?: string;
        duplicateSetVersion?: string;
        groupId?: string;
        dataObjectId?: string;
        dataObjectGroup?: IDataObjectRecord[];
    };
    openSearchForm?: boolean;
    clusteredView?: boolean;
    selectedGroupId: string | null;
    duplicateSearchCommentRecordId?: string | null;
    duplicateSearchAnnotationRecordId?: string | null;
}

interface IMatchParams {
    tenant: string;
}

class DataObjectDuplicateSearch extends Component<IDataObjectDuplicateSearchProps & RouteComponentProps<IMatchParams>, IDataObjectDuplicateSearchStates> {
    constructor(props: IDataObjectDuplicateSearchProps & RouteComponentProps<IMatchParams>) {
        super(props);
        this.state = {
            fetchingPermission: true,
            busyMessage: "Loading data, please wait...",
            selectedDataObject: null, // To Store Selected DataObjectResult
            dataTags: [],
            showSidePanelTabs: false,
            sidePanelActiveTab: "DATASET_RECORD",
            summaryJob: null,
            restartedJob: null,
            deletedJob: null,
            nominatedPreviewDuplicateSetId: null,
            nominatedPreviewDuplicateSetVersion: null,
            nominatedPreviewDuplicateSetGroupId: null,
            mergeRulesProps: {},
            openSearchForm: false,
            clusteredView: true,
            selectedGroupId: null,
            duplicateSearchCommentRecordId: null,
            duplicateSearchAnnotationRecordId: null,
        };
    }

    componentDidMount() {
        this.props.getUserSearchDataObjectsPermission();
        this.props.getUserBusinessAreaDataTags();
        this.setState({
            fetchingCommentsPermission: true,
        });
        this.props.getCommentsPermission();
    }

    componentDidUpdate(prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps, _prevState: unknown, _snapshot: unknown) {
        this.onDeduplicationRunPrepared(prevProps);
        this.onGetUserSearchDataObjectsPermisionResultChanged(prevProps);
        this.onGetDataObjectsDuplicateJobsResultChanged(prevProps);
        this.onGetDataTagsByBusinessAreaIdResultChanged(prevProps);
        this.onGetUserBusinessAreaDataTagsResultChanged(prevProps);
        this.onGetSchemaModelsResultChanged(prevProps);
        this.onGetSchemaDataSetResultChanged(prevProps);
        this.onGetDataObjectResultChanged(prevProps);
        this.onGetSchemaModelResultChanged(prevProps);
        this.onGetDeDuplicatedRecordsRequestResultChanged(prevProps);
        this.onSearchDuplicateDataObjectJobsResultChanged(prevProps);
        this.onGetDuplicateDataObjectsResultSetResultChanged(prevProps);
        this.onGetDuplicateDataObjectsResultSetNextPageResultChanged(prevProps);
        this.onRestartJobResultUpdate(prevProps);
        this.onCancelJobResultUpdate(prevProps);
        this.onRunDeDuplicateJobResultChanged(prevProps);
        this.onRemoveSurvivalRecordNominationResultChanged(prevProps);
        this.onGetCommentsPermissionResultChanged(prevProps);
    }

    onGetCommentsPermissionResultChanged = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (this.props.getCommentsPermissionResult && this.props.getCommentsPermissionResult !== prevProps.getCommentsPermissionResult) {
            if (!this.props.getCommentsPermissionResult.success) {
                showError("Could not retrieve comments permissions.");
            }
            this.setState({
                fetchingCommentsPermission: false,
            });
        }
    };

    onGetUserSearchDataObjectsPermisionResultChanged = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (this.props.getUserSearchDataObjectsPermisionResult && this.props.getUserSearchDataObjectsPermisionResult !== prevProps.getUserSearchDataObjectsPermisionResult) {
            if (!this.props.getUserSearchDataObjectsPermisionResult.success || !this.props.userSearchDataObjectsPermision || !this.props.userSearchDataObjectsPermision.canView) {
                this.setState({
                    permissionDenied: true,
                });
            } else {
                this.setState({
                    fetchingDataObjectsDuplicateJobs: true,
                });
                this.props.getDataObjectsDuplicateJobs();
            }
            this.setState({
                fetchingPermission: false,
            });
        }
    };

    onGetDataObjectsDuplicateJobsResultChanged = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (this.props.getDataObjectsDuplicateJobsResult && this.props.getDataObjectsDuplicateJobsResult !== prevProps.getDataObjectsDuplicateJobsResult) {
            if (!this.props.getDataObjectsDuplicateJobsResult.success) {
                if (this.props.getDataObjectsDuplicateJobsResult.code === "PERMISSION_DENIED") {
                    showError("Unable to get duplicate data objects configuration jobs. Permission denied!");
                } else {
                    showError("Unable to get duplicate data objects configuration jobs at this moment.");
                }
            } else {
                this.selectConfigurationJobInHash();
            }
            this.setState({
                fetchingDataObjectsDuplicateJobs: false,
            });
        }
    };

    onGetDataTagsByBusinessAreaIdResultChanged = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (this.props.getDataTagsByBusinessAreaIdResult && this.props.getDataTagsByBusinessAreaIdResult !== prevProps.getDataTagsByBusinessAreaIdResult) {
            if (!this.props.getDataTagsByBusinessAreaIdResult.success) {
                if (this.props.getDataTagsByBusinessAreaIdResult.code === "PERMISSION_DENIED") {
                    showError("Unable to get data tags. Permission denied!");
                } else {
                    showError("Unable to get data tags at this moment.");
                }
            } else {
                const _dataTags = this.props.dataTagsByBusinessAreaIdResult.Items ?? [];
                // Sort by name
                _dataTags.sort((a, b) => a.tagName.toLowerCase().localeCompare(b.tagName.toLowerCase()));
                this.setState({
                    dataTags: _dataTags,
                });
            }
            this.setState({
                loadingDataTags: false,
            });
        }
    };

    onGetUserBusinessAreaDataTagsResultChanged = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (this.props.getUserBusinessAreaDataTagsResult && this.props.getUserBusinessAreaDataTagsResult !== prevProps.getUserBusinessAreaDataTagsResult) {
            if (!this.props.getUserBusinessAreaDataTagsResult.success) {
                if (this.props.getUserBusinessAreaDataTagsResult.code === "PERMISSION_DENIED") {
                    showError("Unable to get data tags. Permission denied!");
                } else {
                    showError("Unable to get data tags at this moment.");
                }
            } else {
                const _dataTags = _.uniqBy(this.props.userBusinessAreaDataTags.Items ?? [], "tagName");
                // Sort by name
                _dataTags.sort((a, b) => a.tagName.toLowerCase().localeCompare(b.tagName.toLowerCase()));
                this.setState({
                    dataTags: _dataTags,
                });
            }
            this.setState({
                loadingDataTags: false,
            });
        }
    };

    onGetSchemaModelsResultChanged = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (this.props.getSchemaModelsResult && this.props.getSchemaModelsResult !== prevProps.getSchemaModelsResult) {
            this.setState({
                loadingSchemaList: false,
            });
        }
    };

    onGetSchemaDataSetResultChanged = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (this.props.getSchemaDataSetResult && this.props.getSchemaDataSetResult !== prevProps.getSchemaDataSetResult) {
            this.setState({
                loadingDataSetList: false,
            });
        }
    };

    onGetDataObjectResultChanged = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (this.props.getDataObjectResult && this.props.getDataObjectResult !== prevProps.getDataObjectResult) {
            if (!this.props.getDataObjectResult.success) {
                if (this.props.getDataObjectResult.code === "PERMISSION_DENIED") {
                    this.setState({
                        dataObjectPermissionDenied: true,
                    });
                } else {
                    showError("Unable to Retrieve Dataset record.");
                }
            }
            this.setState({
                fetchingDataObject: false,
            });
        }
    };

    onGetSchemaModelResultChanged = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (this.props.getSchemaModelResult && this.props.getSchemaModelResult !== prevProps.getSchemaModelResult) {
            if (!this.props.getSchemaModelResult.success) {
                if (this.props.getSchemaModelResult.code === "PERMISSION_DENIED") {
                    this.setState({
                        schemaPermissionDenied: true,
                    });
                } else {
                    showError("Unable to get schema.");
                }
            }
            this.setState({
                fetchingSchemaModel: false,
            });
        }
    };

    onGetDeDuplicatedRecordsRequestResultChanged = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (this.props.getDeDuplicatedRecordsRequestResult && this.props.getDeDuplicatedRecordsRequestResult !== prevProps.getDeDuplicatedRecordsRequestResult) {
            if (!this.props.getDeDuplicatedRecordsRequestResult.success) {
                showError("Unable to get deduplicated records.");
            }
            this.setState({
                fetchingDeDuplicatedRecords: false,
            });
        }
    };

    onSearchDuplicateDataObjectJobsResultChanged = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (this.props.searchDuplicateDataObjectJobsResult && this.props.searchDuplicateDataObjectJobsResult !== prevProps.searchDuplicateDataObjectJobsResult) {
            if (!this.props.searchDuplicateDataObjectJobsResult.success) {
                if (this.props.getSchemaModelResult.code === "PERMISSION_DENIED") {
                    showError("Permission Denied!");
                } else {
                    showError("Unable to execute search duplicate data objects request.");
                }
            }
            this.setState({
                submittingSearchDuplicateDataObjectRequest: false,
            });
        }
    };

    onGetDuplicateDataObjectsResultSetResultChanged = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (this.props.getDuplicateDataObjectsResultSetResult && this.props.getDuplicateDataObjectsResultSetResult !== prevProps.getDuplicateDataObjectsResultSetResult) {
            if (this.state.selectedDataObject) {
                const newlySelected = { ...this.state.selectedDataObject };
                if (newlySelected.fetchingResultSet) {
                    if (
                        this.props.getDuplicateDataObjectsResultSetResult.resultSet[newlySelected.id] !==
                        prevProps.getDuplicateDataObjectsResultSetResult.resultSet[newlySelected.id]
                    ) {
                        if (this.props.getDuplicateDataObjectsResultSetResult.resultSet[newlySelected.id].success) {
                            this.setState({
                                selectedDataObject: {
                                    ...newlySelected,
                                    fetchingResultSet: false,
                                },
                            });

                            const groupIdFromUrl = this.getGroupIdFromUrl();
                            if (groupIdFromUrl && !this.state.selectedGroupId) {
                                this.openClusterComments(groupIdFromUrl, {
                                    updateUrl: false,
                                    desiredPanel: this.getSidePanelFromUrl(),
                                });
                            }
                        } else {
                            this.setState({
                                selectedDataObject: {
                                    ...newlySelected,
                                    fetchingResultSet: false,
                                    error: this.props.getDuplicateDataObjectsResultSetResult.resultSet[newlySelected.id].message || "Unable to fetch the result set",
                                },
                            });
                        }
                    }
                }
            }
        }
    };

    onGetDuplicateDataObjectsResultSetNextPageResultChanged = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (
            this.props.getDuplicateDataObjectsResultSetNextPageResult &&
            this.props.getDuplicateDataObjectsResultSetNextPageResult !== prevProps.getDuplicateDataObjectsResultSetNextPageResult
        ) {
            if (this.state.selectedDataObject) {
                const newlySelected = { ...this.state.selectedDataObject };
                if (newlySelected.fetchingResultSet) {
                    if (
                        this.props.getDuplicateDataObjectsResultSetNextPageResult.resultSet[newlySelected.id] !==
                        prevProps.getDuplicateDataObjectsResultSetNextPageResult.resultSet[newlySelected.id]
                    ) {
                        if (this.props.getDuplicateDataObjectsResultSetNextPageResult.resultSet[newlySelected.id].success) {
                            this.setState({
                                selectedDataObject: {
                                    ...newlySelected,
                                    fetchingResultSet: false,
                                },
                            });
                        } else {
                            this.setState({
                                selectedDataObject: {
                                    ...newlySelected,
                                    fetchingResultSet: false,
                                    error: this.props.getDuplicateDataObjectsResultSetNextPageResult.resultSet[newlySelected.id].message || "Unable to fetch the result set",
                                },
                            });
                        }
                    }
                } else if (newlySelected.loadingAllData) {
                    if (
                        this.props.getDuplicateDataObjectsResultSetNextPageResult.resultSet[newlySelected.id] !==
                        prevProps.getDuplicateDataObjectsResultSetNextPageResult.resultSet[newlySelected.id]
                    ) {
                        if (this.props.getDuplicateDataObjectsResultSetNextPageResult.resultSet[newlySelected.id].success) {
                            setTimeout(() => {
                                this.onLoadAllRecords(newlySelected.id);
                            }, 200);
                        } else {
                            this.setState({
                                selectedDataObject: {
                                    ...newlySelected,
                                    loadingAllData: false,
                                    error: this.props.getDuplicateDataObjectsResultSetNextPageResult.resultSet[newlySelected.id].message || "Unable to fetch the result set",
                                },
                            });
                        }
                    }
                }
            }
        }
    };

    onRestartJobResultUpdate = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (this.props.restartJobResult && this.props.restartJobResult !== prevProps.restartJobResult) {
            if (!this.props.restartJobResult.success) {
                showError(this.props.restartJobResult.message || "Unable to restart job at this moment.");
            }
            this.setState({
                busy: false,
                busyMessage: "",
            });
        }
    };

    onCancelJobResultUpdate = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (this.props.cancelJobResult && this.props.cancelJobResult !== prevProps.cancelJobResult) {
            if (!this.props.cancelJobResult.success) {
                showError("Unable to cancel job at this moment.");
            }
            this.setState({
                busy: false,
                busyMessage: "",
            });
        }
    };

    onRunDeDuplicateJobResultChanged = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (this.props.runDeDuplicateJobResult && this.props.runDeDuplicateJobResult !== prevProps.runDeDuplicateJobResult) {
            if (!this.props.runDeDuplicateJobResult.success) {
                showError("Unable to create deduplicate job at this moment.");
            } else {
                this.setState({
                    showDeduplicateJobCreatedResult: true,
                });
            }
            this.setState({
                creatingDeduplicationJob: false,
                busyMessage: "",
            });
        }
    };

    onRemoveSurvivalRecordNominationResultChanged = (prevProps: IDataObjectDuplicateSearchProps & RouteComponentProps) => {
        if (this.props.removeSurvivalRecordNominationResult && this.props.removeSurvivalRecordNominationResult !== prevProps.removeSurvivalRecordNominationResult) {
            if (!this.props.removeSurvivalRecordNominationResult.success) {
                showError("Unable to remove nomination at the moment.");
            }
        }
    };

    onSidePanelActiveTabChanged = (activeKey: string) => {
        const panel = activeKey === "COMMENTS" ? "comments" : activeKey === "ANNOTATIONS" ? "annotations" : null;
        if (panel && this.state.selectedGroupId) {
            this.setUrlClusterSelection(this.state.selectedGroupId, panel);
        }
        this.setState({
            sidePanelActiveTab: activeKey,
        });
    };

    search = (searchInput: IDataObjectDuplicateSearchInput) => {
        if (searchInput.dataTags && searchInput.dataTags.length > 0) {
            this.props.searchDuplicateDataObjects(
                searchInput.name,
                searchInput.description,
                searchInput.businessArea,
                searchInput.schema,
                searchInput.dataSet,
                searchInput.dataTags,
            );
            this.setState({
                selectedDataObject: null,
                busyMessage: "Submitting search duplicate data objects request...",
                submittingSearchDuplicateDataObjectRequest: true,
                showSidePanelTabs: false,
                summaryJob: null,
                showDataObjectFormContainer: false,
            });
        }
        this.setState({
            searchInput,
        });
    };

    onBusinessAreaChange = (businessAreaId: string) => {
        this.setState({
            loadingSchemaList: true,
            loadingDataTags: true,
        });
        if (businessAreaId === "all") {
            if (this.props.userBusinessAreaDataTags && this.props.userBusinessAreaDataTags.Items && this.props.userBusinessAreaDataTags.Items.length > 0) {
                let dataTags = _.uniqBy(this.props.userBusinessAreaDataTags.Items, "tagName");
                this.setState({
                    dataTags,
                    loadingDataTags: false,
                });
            } else {
                this.props.getUserBusinessAreaDataTags();
            }
        } else {
            this.props.getDataTagsByBusinessAreaId(businessAreaId);
        }
        this.props.getSchemaModels(businessAreaId);
    };

    onSchemaChange = (businessAreaId: string, schemaId: string) => {
        this.setState({
            loadingDataSetList: true,
        });
        this.props.getDataSetBySchemaId(businessAreaId, schemaId);
    };

    selectConfigurationJobInHash = () => {
        if (this.props.history.location.hash) {
            let hashValue = decodeURIComponent(this.props.history.location.hash);
            if (this.props.dataObjectsDuplicateJobsResult.Items && this.props.dataObjectsDuplicateJobsResult.Items.length > 0) {
                const hashWithoutPrefix = hashValue.startsWith("#") ? hashValue.slice(1) : hashValue;
                const [selectedConfigurationId] = hashWithoutPrefix.split("?");
                if (selectedConfigurationId) {
                    let selectedConfigurationJob = this.props.dataObjectsDuplicateJobsResult.Items.find((job) => job.jobId === selectedConfigurationId);
                    if (selectedConfigurationJob) {
                        // Preserve any deep-link hash params (e.g. groupId/panel) from notifications.
                        this.onViewDuplicateDataObjectResultSet(selectedConfigurationJob, { preserveExistingHash: true });
                        return;
                    }
                }
            }
            this.props.history.push(this.props.history.location.pathname);
        }
    };

    onViewDuplicateDataObjectResultSet = (job: IDataObjectsDuplicateJob, options?: { preserveExistingHash?: boolean }) => {
        this.setState({
            selectedDataObject: {
                name: job.name,
                id: job.jobId,
                job: job,
                dataTags: job.jobInfo.dataTags,
                fetchingResultSet: true,
                error: null,
            },
        });
        this.props.getDuplicateDataObjectsResultSet(job.jobId);
        this.setState({
            summaryJob: job,
        });

        // When coming from a notification deep-link, do not clobber the existing hash query.
        if (!options?.preserveExistingHash) {
            this.props.history.push(`#${job.jobId}`);
        }
    };

    restartJob = (job: IDataObjectsDuplicateJob) => {
        this.setState({
            restartedJob: job,
            showJobRestartConfirmation: true,
            jobRestartConfirmMessage: `Are you sure, you want to run '${job.name}' search config job again?`,
        });
    };

    onRestartJobConfirmed = () => {
        this.setState({
            busy: true,
            busyMessage: "Please wait...",
        });
        if (this.state.restartedJob) {
            this.props.restartJob(this.state.restartedJob.jobId, this.state.restartedJob.version, true, false);
        }
        this.onRestartJobCancel();
    };

    onRestartJobCancel = () => {
        this.setState({
            restartedJob: null,
            showJobRestartConfirmation: false,
            jobRestartConfirmMessage: "",
        });
    };

    cancelJob = (jobId: string, version: string) => {
        this.setState({
            busy: true,
            busyMessage: "Please wait...",
        });
        this.props.cancelJob(jobId, version);
    };

    deleteJob = (job: IDataObjectsDuplicateJob) => {
        this.setState({
            deletedJob: job,
            showJobDeleteConfirmation: true,
            deleteJobConfirmationMessage: `Are you sure, you want to delete '${job.name}' search config?`,
        });
    };

    onDeleteJobConfirmed = () => {
        this.setState({
            busy: true,
            busyMessage: "Please wait...",
        });
        if (this.state.deletedJob) {
            this.props.restartJob(this.state.deletedJob.jobId, this.state.deletedJob.version, true, true);
        }
        this.onDeleteJobCancel();
    };

    onDeleteJobCancel = () => {
        this.setState({
            deletedJob: null,
            showJobDeleteConfirmation: false,
            deleteJobConfirmationMessage: "",
        });
    };

    onDataObjectSelected = (dataObject?: IDataObjectRecord) => {
        if (this.state.selectedDataObject && dataObject) {
            const newlySelected = { ...this.state.selectedDataObject };
            this.setState({
                selectedDataObject: {
                    ...newlySelected,
                    selectedDataObject: dataObject,
                },
            });
            this.getSelectedDataObject(dataObject);
        }
    };

    getDuplicateSearchCommentRecordId = (dataObject?: IDataObjectRecord | null) => {
        return dataObject?._duplicateSetId && dataObject?._groupId && dataObject?._duplicateSetVersion
            ? `dupSet:${dataObject._duplicateSetId}:group:${dataObject._groupId}:v:${dataObject._duplicateSetVersion}`
            : null;
    };

    getHashParams = () => {
        const rawHash = decodeURIComponent(this.props.history.location.hash || "");
        const hashWithoutPrefix = rawHash.startsWith("#") ? rawHash.slice(1) : rawHash;
        const [jobIdPart, queryPart] = hashWithoutPrefix.split("?");
        const params = new URLSearchParams(queryPart || "");
        return {
            jobId: jobIdPart || null,
            groupId: params.get("groupId"),
            panel: params.get("panel"),
        };
    };

    getGroupIdFromUrl = () => {
        const { groupId } = this.getHashParams();
        if (groupId) return groupId;
        const params = new URLSearchParams(this.props.history.location.search);
        return params.get("groupId");
    };

    getSidePanelFromUrl = () => {
        const { panel } = this.getHashParams();
        const rawPanel = panel || new URLSearchParams(this.props.history.location.search).get("panel");
        if (!rawPanel) return rawPanel;
        return rawPanel.replace(/^-/g, "").toLowerCase();
    };

    getAnnotationsInitialFiltersFromUrl = () => {
        try {
            const rawHash = decodeURIComponent(this.props.history.location.hash || "");
            const hashWithoutPrefix = rawHash.startsWith("#") ? rawHash.slice(1) : rawHash;
            const [, hashQueryPart] = hashWithoutPrefix.split("?");
            const params = new URLSearchParams(hashQueryPart || "");
            const closedParam = params.get("closed") || new URLSearchParams(this.props.history.location.search).get("closed");
            const shouldFilterClosed = !!(closedParam && closedParam.toLowerCase() === "true");
            return shouldFilterClosed ? { closed: true } : null;
        } catch (e) {
            return null;
        }
    };

    setUrlClusterSelection = (groupId: string | null, panel: string | null) => {
        const location = this.props.history.location;
        const { jobId } = this.getHashParams();
        const effectiveJobId = jobId || this.state.selectedDataObject?.id || null;
        if (!effectiveJobId) return;

        const rawHash = decodeURIComponent(location.hash || "");
        const hashWithoutPrefix = rawHash.startsWith("#") ? rawHash.slice(1) : rawHash;
        const [existingJobIdPart, existingQueryPart] = hashWithoutPrefix.split("?");
        const params = new URLSearchParams(existingJobIdPart && existingJobIdPart === effectiveJobId ? existingQueryPart || "" : "");

        if (groupId) params.set("groupId", groupId);
        else params.delete("groupId");

        if (panel) params.set("panel", panel);
        else params.delete("panel");

        const query = params.toString();
        const nextHash = query ? `#${effectiveJobId}?${query}` : `#${effectiveJobId}`;

        this.props.history.replace({
            ...location,
            hash: nextHash,
        });
    };

    openClusterComments = (groupId: string, options?: { updateUrl?: boolean; desiredPanel?: string | null }) => {
        const jobId = this.state.selectedDataObject?.id;
        const itemsForJob = jobId ? this.props.duplicateDataObjectsResultSet?.resultSet?.[jobId]?.Items : null;
        const representativeRecord = itemsForJob?.find((r) => r?._groupId === groupId);
        const duplicateSearchCommentRecordId = this.getDuplicateSearchCommentRecordId(representativeRecord);

        const canShowComments = !!(this.props.userCommentsPermission?.canView && duplicateSearchCommentRecordId);
        const canShowAnnotations = !!(this.props.userCommentsPermission?.canView && duplicateSearchCommentRecordId);
        const desiredPanel = (options?.desiredPanel ?? "comments").replace(/^-/g, "").toLowerCase();
        const wantsComments = desiredPanel === "comments";
        const wantsAnnotations = desiredPanel === "annotations";

        const nextTab = canShowComments && wantsComments ? "COMMENTS" : canShowAnnotations && wantsAnnotations ? "ANNOTATIONS" : "SUMMARY";

        const shouldUpdateUrl = options?.updateUrl ?? true;
        if (shouldUpdateUrl) {
            this.setUrlClusterSelection(groupId, desiredPanel);
        }

        this.setState({
            selectedGroupId: groupId,
            duplicateSearchCommentRecordId,
            duplicateSearchAnnotationRecordId: duplicateSearchCommentRecordId,
            showSidePanelTabs: true,
            showDataObjectFormContainer: false,
            sidePanelActiveTab: nextTab,
        });
    };

    getSelectedDataObject = (dataObject: IDataObjectRecord) => {
        const duplicateSearchCommentRecordId = this.getDuplicateSearchCommentRecordId(dataObject);

        this.setState({
            showDataObjectFormContainer: true,
            showSidePanelTabs: true,
            duplicateSearchCommentRecordId,
            duplicateSearchAnnotationRecordId: duplicateSearchCommentRecordId,
        });
        this.props.getSchemaModel(dataObject._businessAreaId, dataObject._schemaId);
        this.props.getDataObject(dataObject._businessAreaId, dataObject._schemaId, dataObject._dataSetId, dataObject._dataObjectId);
        this.props.getDeDuplicatedRecords(dataObject._dataObjectId);
        this.setState({
            schemaPermissionDenied: false,
            dataObjectPermissionDenied: false,
            fetchingSchemaModel: true,
            fetchingDataObject: true,
            fetchingDeDuplicatedRecords: true,
            sidePanelActiveTab: "DATASET_RECORD",
        });
    };

    onDuplicateDataObjectsResultSetPageSelect = (pagination: IPagination, jobId: string) => {
        if (
            this.props.duplicateDataObjectsResultSet.resultSet[jobId].pageKey &&
            pagination.current * pagination.pageSize >= this.props.duplicateDataObjectsResultSet.resultSet[jobId].Items.length
        ) {
            if (this.state.selectedDataObject?.id === jobId && !this.state.selectedDataObject.loadingAllData) {
                const selected = { ...this.state.selectedDataObject };
                this.setState({
                    selectedDataObject: {
                        ...selected,
                        fetchingResultSet: true,
                        error: null,
                    },
                });
                this.props.getDuplicateDataObjectsResultSetNextPage(jobId, this.props.duplicateDataObjectsResultSet.resultSet[jobId].pageKey);
            }
        }
    };

    onLoadAllRecords = (jobId: string) => {
        if (this.props.duplicateDataObjectsResultSet && this.props.duplicateDataObjectsResultSet.resultSet[jobId]) {
            if (this.state.selectedDataObject) {
                const selected = { ...this.state.selectedDataObject };
                if (selected.id === jobId) {
                    if (this.props.duplicateDataObjectsResultSet.resultSet[jobId].pageKey) {
                        this.setState({
                            selectedDataObject: {
                                ...selected,
                                loadingAllData: true,
                                error: null,
                            },
                        });
                        this.props.getDuplicateDataObjectsResultSetNextPage(jobId, this.props.duplicateDataObjectsResultSet.resultSet[jobId].pageKey);
                    } else {
                        this.setState({
                            selectedDataObject: {
                                ...selected,
                                loadingAllData: false,
                                error: null,
                            },
                        });
                    }
                }
            }
        }
    };

    onNominateSurvivor = (dataObject: IDataObjectRecord, deduplicationOption: string) => {
        this.props.nominateSurvivalRecord(dataObject._duplicateSetId, dataObject._duplicateSetVersion, dataObject._groupId, dataObject._dataObjectId, deduplicationOption);
    };

    onRemoveSurvivorNomination = (dataObject: IDataObjectRecord) => {
        this.props.removeSurvivalRecordNomination(dataObject._duplicateSetId, dataObject._duplicateSetVersion, dataObject._groupId, dataObject._dataObjectId);
    };

    onPreviewClick = (dataObject: IDataObjectRecord) => {
        this.setState({
            nominatedPreviewDuplicateSetId: dataObject._duplicateSetId,
            nominatedPreviewDuplicateSetVersion: dataObject._duplicateSetVersion,
            nominatedPreviewDuplicateSetGroupId: dataObject._groupId,
            sidePanelActiveTab: "DATASET_RECORD",
        });
    };

    onPreviewClose = () => {
        this.setState({
            nominatedPreviewDuplicateSetId: null,
            nominatedPreviewDuplicateSetVersion: null,
            nominatedPreviewDuplicateSetGroupId: null,
        });
    };

    onMergeRulesClick = (dataObject: IDataObjectRecord, dataObjectGroup: IDataObjectRecord[]) => {
        this.onPreviewClose(); // Close preview first
        this.setState({
            showMergeRules: true,
            mergeRulesProps: {
                businessAreaId: dataObject._businessAreaId,
                schemaId: dataObject._schemaId,
                duplicateSetId: dataObject._duplicateSetId,
                duplicateSetVersion: dataObject._duplicateSetVersion,
                groupId: dataObject._groupId,
                dataObjectId: dataObject._dataObjectId,
                dataObjectGroup,
            },
        });
    };

    onMergeRulesClose = () => {
        this.setState({
            showMergeRules: false,
            mergeRulesProps: {},
        });
    };

    onShowSummary = (job: IDataObjectsDuplicateJob) => {
        this.setState({
            showDataObjectFormContainer: false,
            showSidePanelTabs: true,
            sidePanelActiveTab: "SUMMARY",
            summaryJob: job,
        });
    };

    onGoBackToSearchConfig = () => {
        const location = this.props.history.location;
        this.props.history.push(location.pathname);
        this.setUrlClusterSelection(null, null);
        this.setState({
            selectedDataObject: null,
            selectedGroupId: null,
            showSidePanelTabs: false,
            duplicateSearchCommentRecordId: null,
            duplicateSearchAnnotationRecordId: null,
        });
    };

    goToJobs = () => {
        this.props.history.push(AppPaths.TENANT_MONITOR_ITEM.replace(":tenant", this.props.match.params.tenant.toLowerCase()).replace(":monitorItem", "jobs"));
    };

    closeDeduplicateJobCreatedResult = () => {
        this.setState({
            showDeduplicateJobCreatedResult: false,
        });
    };

    // The tenant's Golden Nominal maintenance switch is read first: with it on, the run asks
    // whether to delete duplicates (onDeduplicationRunPrepared); otherwise it starts straight away
    // and deletes them, as before.
    runDeduplicateJob = (duplicateSetId: string) => {
        this.setState({ creatingDeduplicationJob: true });
        this.props.prepareDeduplicationRun(duplicateSetId);
    };

    onDeduplicationRunPrepared = (prevProps: IDataObjectDuplicateSearchProps) => {
        const preparation = this.props.deduplicationRunPreparation;
        if (preparation && preparation !== prevProps.deduplicationRunPreparation) {
            if (preparation.askOptions) {
                this.setState({ creatingDeduplicationJob: false, deduplicationOptionsFor: preparation.duplicateSetId });
            }
            this.props.clearDeduplicationRunPreparation();
        }
    };

    runDeduplicationWithOptions = (deleteDuplicates: IRunDeduplicationOptions) => {
        const duplicateSetId = this.state.deduplicationOptionsFor;
        // Already started (a second click while the dialog closes).
        if (!duplicateSetId) {
            return;
        }
        this.setState({ creatingDeduplicationJob: true, deduplicationOptionsFor: null });
        this.props.runDeDuplicateJob(duplicateSetId, deleteDuplicates);
    };

    isBusy = () => {
        return (
            this.state.fetchingPermission ||
            this.state.fetchingDataObjectsDuplicateJobs ||
            this.state.submittingSearchDuplicateDataObjectRequest ||
            this.state.creatingDeduplicationJob ||
            this.state.busy
        );
    };

    render() {
        return this.isBusy() ? (
            <LoadingOverlay busy message={this.state.busyMessage || "Loading data, please wait..."}></LoadingOverlay>
        ) : this.state.permissionDenied ? (
            <ProhibitedArea></ProhibitedArea>
        ) : (
            <>
                <DataObjectDuplicateSearchPageBreadcrumb setBreadcrumbNavigationItems={this.props.setBreadcrumbNavigationItems} onBackToList={this.onGoBackToSearchConfig} />
                {this.state.showMergeRules && <DataObjectDuplicateMergeRules onClose={this.onMergeRulesClose} {...this.state.mergeRulesProps}></DataObjectDuplicateMergeRules>}
                <div className="relative flex flex-1 flex-row gap-4 overflow-auto">
                    <div className="relative flex flex-1 flex-col overflow-auto">
                        {this.state.showJobDeleteConfirmation && (
                            <OkCancelConfirmationBox
                                show={this.state.showJobDeleteConfirmation}
                                message={this.state.deleteJobConfirmationMessage}
                                onCancel={this.onDeleteJobCancel}
                                onOk={this.onDeleteJobConfirmed}
                            ></OkCancelConfirmationBox>
                        )}
                        {this.state.showJobRestartConfirmation && (
                            <OkCancelConfirmationBox
                                show={this.state.showJobRestartConfirmation}
                                message={this.state.jobRestartConfirmMessage}
                                onCancel={this.onRestartJobCancel}
                                onOk={this.onRestartJobConfirmed}
                            ></OkCancelConfirmationBox>
                        )}
                        <RunDeduplicationModal
                            open={!!this.state.deduplicationOptionsFor}
                            onRun={this.runDeduplicationWithOptions}
                            onCancel={() => this.setState({ deduplicationOptionsFor: null })}
                        />
                        <SuccessResult
                            show={this.state.showDeduplicateJobCreatedResult}
                            title="Deduplication job created!"
                            okText="Go to jobs"
                            cancelText="Close"
                            onOkClick={this.goToJobs}
                            onCancelClick={this.closeDeduplicateJobCreatedResult}
                        ></SuccessResult>
                        <FlexContainerLayout
                            headerClassName="*:w-full"
                            header={
                                <div className="flex flex-col">
                                    <div className="flex flex-col gap-3 md:flex-row md:items-center">
                                        {!!this.state.selectedDataObject?.job &&
                                            (this.state.selectedGroupId ? (
                                                <Button
                                                    onClick={() =>
                                                        this.setState(
                                                            {
                                                                selectedGroupId: null,
                                                                showSidePanelTabs: false,
                                                                showDataObjectFormContainer: false,
                                                                duplicateSearchCommentRecordId: null,
                                                                duplicateSearchAnnotationRecordId: null,
                                                                sidePanelActiveTab: "DATASET_RECORD",
                                                            },
                                                            () => this.setUrlClusterSelection(null, null),
                                                        )
                                                    }
                                                    icon={<CaretLeftOutlined />}
                                                    iconPlacement="start"
                                                    data-testid={CypressTestIds.DUPLICATE_SEARCH_CONFIG_LIST_BACK_BUTTON}
                                                >
                                                    BACK TO JOB
                                                </Button>
                                            ) : (
                                                <Button
                                                    onClick={this.onGoBackToSearchConfig}
                                                    icon={<CaretLeftOutlined />}
                                                    iconPlacement="start"
                                                    data-testid={CypressTestIds.DUPLICATE_SEARCH_CONFIG_LIST_BACK_BUTTON}
                                                >
                                                    BACK TO LIST
                                                </Button>
                                            ))}
                                        {!this.state.selectedDataObject?.job && (
                                            <Typography.Title level={3} className={this.state.openSearchForm ? "!mb-4" : "!mb-0"}>
                                                Search Duplicates
                                            </Typography.Title>
                                        )}
                                        {!!this.state.selectedDataObject?.job?.jobId && (
                                            <Tooltip placement="topLeft" title="Run deduplication job">
                                                <Button
                                                    type="primary"
                                                    icon={<CaretRightFilled />}
                                                    onClick={() => this.runDeduplicateJob(this.state.selectedDataObject?.job?.jobId ?? "")}
                                                >
                                                    Run Deduplication
                                                </Button>
                                            </Tooltip>
                                        )}
                                        {!!this.state.selectedDataObject?.job?.jobId && <BirthToggle definitionId={this.state.selectedDataObject.job.jobId} />}
                                        {!this.state.selectedDataObject?.job && !this.state.openSearchForm && (
                                            <div className="flex-none">
                                                <Button
                                                    type="primary"
                                                    data-testid={CypressTestIds.DUPLICATE_SEARCH_CONFIG_COLLAPSE_BUTTON}
                                                    onClick={() => this.setState({ openSearchForm: true })}
                                                >
                                                    Create New Search
                                                </Button>
                                            </div>
                                        )}
                                        <div className="flex-1" />
                                        {!this.state.selectedDataObject && (
                                            <Radio.Group
                                                value={this.state.clusteredView ? "clustered" : "sequenced"}
                                                onChange={(e) =>
                                                    this.setState({
                                                        clusteredView: e.target.value === "clustered",
                                                    })
                                                }
                                            >
                                                <Radio.Button value="clustered" onClick={() => this.onGoBackToSearchConfig()}>
                                                    <AppstoreOutlined />
                                                    <span data-testid={CypressTestIds.DUPLICATE_SEARCH_CLUSTERED_VIEW_BUTTON} className="ml-1">
                                                        Clustered View
                                                    </span>
                                                </Radio.Button>
                                                <Radio.Button value="sequenced" onClick={() => this.onGoBackToSearchConfig()}>
                                                    <BarsOutlined />
                                                    <span data-testid={CypressTestIds.DUPLICATE_SEARCH_SEQUENCED_VIEW_BUTTON} className="ml-1">
                                                        Sequenced List
                                                    </span>
                                                </Radio.Button>
                                            </Radio.Group>
                                        )}
                                    </div>
                                    {this.state.openSearchForm && (
                                        <Segment shadow={false} className="bg-slate-100 p-3">
                                            <DataObjectSearchDuplicateForm
                                                onSearch={this.search}
                                                onCancel={() => this.setState({ openSearchForm: false })}
                                                businessAreas={this.props.userBusinessAreas}
                                                onBusinessAreaChange={this.onBusinessAreaChange}
                                                loadingSchemaList={this.state.loadingSchemaList}
                                                schemaList={this.props.schemaModelListResult}
                                                onSchemaChange={this.onSchemaChange}
                                                loadingDataSetList={this.state.loadingDataSetList}
                                                dataSetList={this.props.schemaDataSetResult}
                                                loadingDataTags={this.state.loadingDataTags}
                                                dataTags={this.state.dataTags}
                                            />
                                        </Segment>
                                    )}
                                </div>
                            }
                        >
                            {!this.state.selectedDataObject && !this.isBusy() && (
                                <>
                                    <h4 className="mb-2 font-bold">Previous Search Results</h4>
                                    {this.state.clusteredView ? (
                                        <div className="scroll-webkit flex h-full flex-1 flex-col overflow-auto px-1 pb-4">
                                            <DuplicateDataObjectJobClusteredView
                                                jobList={this.props.dataObjectsDuplicateJobsResult.Items}
                                                onViewData={this.onViewDuplicateDataObjectResultSet}
                                                onRestartJob={this.restartJob}
                                                onCancelJob={this.cancelJob}
                                                onDeleteJob={this.deleteJob}
                                            />
                                        </div>
                                    ) : (
                                        <div className="h-full overflow-auto">
                                            <DuplicateDataObjectJobList
                                                jobList={this.props.dataObjectsDuplicateJobsResult.Items}
                                                onViewData={this.onViewDuplicateDataObjectResultSet}
                                                onRestartJob={this.restartJob}
                                                onCancelJob={this.cancelJob}
                                                onDeleteJob={this.deleteJob}
                                            ></DuplicateDataObjectJobList>
                                        </div>
                                    )}
                                </>
                            )}
                            {this.state.selectedDataObject?.fetchingResultSet && (
                                <LoadingOverlay busy={this.state.selectedDataObject.fetchingResultSet} message={"Getting result set..."}></LoadingOverlay>
                            )}
                            {this.state.selectedDataObject && !this.state.selectedDataObject.fetchingResultSet && !!this.state.selectedDataObject.job && (
                                <>
                                    {this.state.clusteredView ? (
                                        this.state.selectedGroupId ? (
                                            <DuplicateDataObjectComparison
                                                dataTags={this.state.selectedDataObject.dataTags}
                                                dataObjectList={
                                                    (this.props.duplicateDataObjectsResultSet.resultSet[this.state.selectedDataObject.id] &&
                                                        this.props.duplicateDataObjectsResultSet.resultSet[this.state.selectedDataObject.id].Items &&
                                                        this.props.duplicateDataObjectsResultSet.resultSet[this.state.selectedDataObject.id].Items.filter(
                                                            (d) => d._groupId === this.state.selectedGroupId,
                                                        )) ||
                                                    []
                                                }
                                                onNominateSurvivor={this.onNominateSurvivor}
                                                onRemoveSurvivorNomination={this.onRemoveSurvivorNomination}
                                                onPreviewClick={this.onPreviewClick}
                                                onMergeRulesClick={this.onMergeRulesClick}
                                            />
                                        ) : (
                                            <DuplicateDataObjectRecordClusteredView
                                                job={this.state.selectedDataObject.job}
                                                dataTags={this.state.selectedDataObject.dataTags}
                                                dataObjectList={
                                                    (this.props.duplicateDataObjectsResultSet.resultSet[this.state.selectedDataObject.id] &&
                                                        this.props.duplicateDataObjectsResultSet.resultSet[this.state.selectedDataObject.id].Items) ||
                                                    []
                                                }
                                                onSelectGroupId={(id) => (id ? this.openClusterComments(id) : null)}
                                                onShowSummaryClick={this.onShowSummary}
                                                loadingMore={this.state.selectedDataObject.loadingAllData}
                                                onRemoveSurvivorNomination={this.onRemoveSurvivorNomination}
                                                onPreviewClick={this.onPreviewClick}
                                                onMergeRulesClick={this.onMergeRulesClick}
                                            />
                                        )
                                    ) : (
                                        <DuplicateDataObjectList
                                            job={this.state.selectedDataObject.job}
                                            selectedGroupId={this.state.selectedGroupId}
                                            dataTags={this.state.selectedDataObject.dataTags}
                                            dataObjectList={
                                                (this.props.duplicateDataObjectsResultSet.resultSet[this.state.selectedDataObject.id] &&
                                                    this.props.duplicateDataObjectsResultSet.resultSet[this.state.selectedDataObject.id].Items) ||
                                                []
                                            }
                                            onDataObjectSelected={this.onDataObjectSelected}
                                            onPageSelect={this.onDuplicateDataObjectsResultSetPageSelect}
                                            onShowSummaryClick={this.onShowSummary}
                                            loadAllRecords={this.onLoadAllRecords}
                                            loadingMore={this.state.selectedDataObject.loadingAllData}
                                            onNominateSurvivor={this.onNominateSurvivor}
                                            onRemoveSurvivorNomination={this.onRemoveSurvivorNomination}
                                            onPreviewClick={this.onPreviewClick}
                                            onMergeRulesClick={this.onMergeRulesClick}
                                            onSelectGroupId={(id) => (id ? this.openClusterComments(id) : null)}
                                        ></DuplicateDataObjectList>
                                    )}
                                </>
                            )}
                        </FlexContainerLayout>
                    </div>
                    {this.state.nominatedPreviewDuplicateSetId && (
                        <SidePanel>
                            <DataObjectDuplicateMergePreview
                                duplicateSetId={this.state.nominatedPreviewDuplicateSetId}
                                duplicateSetVersion={this.state.nominatedPreviewDuplicateSetVersion}
                                groupId={this.state.nominatedPreviewDuplicateSetGroupId}
                                onClose={this.onPreviewClose}
                            ></DataObjectDuplicateMergePreview>
                        </SidePanel>
                    )}
                    {this.state.showSidePanelTabs && (
                        <SidePanel>
                            <MenuPanelContainer
                                recordKeys={{
                                    ...(this.state.showDataObjectFormContainer ? { DATASET_RECORD: "Dataset Record" } : {}),
                                    ...(this.props.dataObject ? { ATTACHMENTS: "Attachments" } : {}),
                                    ...(this.props.userCommentsPermission?.canView && this.state.duplicateSearchCommentRecordId ? { COMMENTS: "Comments" } : {}),
                                    ...(this.props.userCommentsPermission?.canView && this.state.duplicateSearchAnnotationRecordId ? { ANNOTATIONS: "Annotations" } : {}),
                                    SUMMARY: "Summary",
                                    DEDUPLICATION_HISTORY: "Deduplication History",
                                }}
                                selectedKey={this.state.sidePanelActiveTab}
                                onChange={this.onSidePanelActiveTabChanged}
                                footer={
                                    (this.state.sidePanelActiveTab === "SUMMARY" || this.state.sidePanelActiveTab === "DEDUPLICATION_HISTORY") && (
                                        <div className="flex justify-end bg-slate-50 p-2">
                                            <Button onClick={() => this.setState({ showSidePanelTabs: false })}>Close</Button>
                                        </div>
                                    )
                                }
                            >
                                {this.state.sidePanelActiveTab === "DATASET_RECORD" &&
                                    (this.state.showDataObjectFormContainer ? (
                                        this.state.fetchingSchemaModel || this.state.fetchingDataObject ? (
                                            <LoadingOverlay busy={true} message="Getting dataset record..."></LoadingOverlay>
                                        ) : this.state.dataObjectPermissionDenied || this.state.schemaPermissionDenied ? (
                                            <ProhibitedArea></ProhibitedArea>
                                        ) : this.props.dataObject ? (
                                            <BasicFormBuilder
                                                key={this.props.dataObject.dataObjectId}
                                                formDataObject={this.props.dataObject}
                                                schemaFields={this.props.schemaModel?.fields || []}
                                                readOnly={true}
                                                onCancel={() => {
                                                    this.setState({
                                                        showSidePanelTabs: false,
                                                    });
                                                }}
                                                showHeader={false}
                                            ></BasicFormBuilder>
                                        ) : (
                                            <Empty description="No record selected" />
                                        )
                                    ) : (
                                        <Empty description="No record selected" />
                                    ))}
                                {this.state.sidePanelActiveTab === "ATTACHMENTS" &&
                                    (this.props.dataObject ? (
                                        <AttachmentTab
                                            dataObject={this.props.dataObject}
                                            schemaFields={(this.props.schemaModel?.fields || []) as any}
                                            onDownload={(file: any) => console.log("Download attachment:", file)}
                                        />
                                    ) : (
                                        <Empty description="No record selected" />
                                    ))}
                                {this.state.sidePanelActiveTab === "COMMENTS" &&
                                    (this.state.duplicateSearchCommentRecordId ? (
                                        <Comments
                                            key={this.state.duplicateSearchCommentRecordId}
                                            recordId={this.state.duplicateSearchCommentRecordId}
                                            permission={this.props.userCommentsPermission}
                                            recordType="duplicateSearch"
                                        />
                                    ) : (
                                        <Empty description="No record selected" />
                                    ))}

                                {this.state.sidePanelActiveTab === "ANNOTATIONS" &&
                                    (this.state.duplicateSearchAnnotationRecordId ? (
                                        <Annotations
                                            key={`${this.state.duplicateSearchAnnotationRecordId}-annotations`}
                                            recordId={this.state.duplicateSearchAnnotationRecordId}
                                            recordType="duplicateSearch"
                                            permission={this.props.userCommentsPermission}
                                            fields={[]}
                                            historyDisplayMode="modal"
                                            initialServerFilters={this.getAnnotationsInitialFiltersFromUrl()}
                                        />
                                    ) : (
                                        <Empty description="No record selected" />
                                    ))}
                                {this.state.sidePanelActiveTab === "SUMMARY" && (
                                    <DataObjectDuplicateSummary
                                        key={this.state.summaryJob?.jobId}
                                        jobId={this.state.summaryJob?.jobId}
                                        clusterSummary={this.state.summaryJob?.runInfo?.clusterSummary}
                                    ></DataObjectDuplicateSummary>
                                )}
                                {this.state.sidePanelActiveTab === "DEDUPLICATION_HISTORY" &&
                                    (this.state.fetchingDeDuplicatedRecords ? (
                                        <LoadingOverlay busy={true} message="Getting records..."></LoadingOverlay>
                                    ) : (
                                        <DeduplicatedRecordsTable items={this.props.deDuplicatedRecordsResult.Items}></DeduplicatedRecordsTable>
                                    ))}
                            </MenuPanelContainer>
                        </SidePanel>
                    )}
                </div>
            </>
        );
    }
}

export default connector(withRouter(DataObjectDuplicateSearch));
